/**
 * The short wall rule (PRP-SKETCH-FIELD-3 H8, J13): a door or a missing wall is
 * never refused because the wall is short.
 *
 * - A missing wall on a wall shorter than its width takes the wall's full length.
 * - A door on a wall shorter than its width plus a jamb each side
 *   (`door_jamb_clear_in`) takes the wall's length minus two jambs, centred.
 *
 * Used by tool placement (`placeOpening`), `addOpening`, the kind switch and
 * (exported for the walls lane) closet doors. Windows keep their FIELD-2 refusal.
 */
import { POLICY, inToFt } from './policy';
import { EPS } from './geom';
import type { OpeningKind } from './types';

/** One jamb's clearance, feet. */
export const jambFt = (): number => inToFt(POLICY.door_jamb_clear_in);

export interface ShortFit { offFt: number; widthFt: number }

/**
 * Where a door or missing wall of width `w` goes on a wall `L` long when the wall
 * is too short for it; null when the wall takes it as it is (or for a window).
 */
export function shortWallFit(L: number, kind: OpeningKind, w: number): ShortFit | null {
  if (kind === 'window' || !(L > 0)) return null;
  if (kind === 'missing') return L < w - EPS ? { offFt: 0, widthFt: L } : null;
  const j = jambFt();
  if (L >= w + 2 * j - EPS) return null;
  // A wall no wider than its two jambs still takes a door: the whole wall.
  const width = L - 2 * j >= POLICY.min_segment_ft ? L - 2 * j : L;
  return { offFt: (L - width) / 2, widthFt: width };
}

/**
 * A door that replaces a missing wall filling its whole wall (J5): the wall's door
 * default width, centred, or the H8 short fit when the wall is short.
 */
export function doorOnWholeWall(L: number, defaultW: number): ShortFit {
  return shortWallFit(L, 'door', defaultW) ?? { offFt: (L - defaultW) / 2, widthFt: defaultW };
}
